import React, {FC, useEffect, useState} from "react";
import {RealizedReportDetailRow, RealizedReportRow} from "../../models/RealizedReport";
import GatewayService from "../../service/GatewayService";


const DEFAULT_REPORT_CLIENT_ID = "ECA007";
const DEFAULT_REPORT_FROM = "2025-04-01";
const DEFAULT_REPORT_TO = "2026-03-31";

const formatMoney = (value: number) => value.toFixed(2);
type DetailSortBy = "entryDate" | "exitDate" | "profit";

const fetchReportData = (from: string, to: string, clientId: string) => Promise.all([
    GatewayService.getRealizedReport(from, to, clientId),
    GatewayService.getRealizedReportDetail(from, to, clientId),
]);

export const ReportComponent: FC = () => {
    const [clientId, setClientId] = useState(DEFAULT_REPORT_CLIENT_ID);
    const [from, setFrom] = useState(DEFAULT_REPORT_FROM);
    const [to, setTo] = useState(DEFAULT_REPORT_TO);
    const [symbolFilter, setSymbolFilter] = useState("");
    const [entryDateFilter, setEntryDateFilter] = useState("");
    const [exitDateFilter, setExitDateFilter] = useState("");
    const [realizedPnlAscending, setRealizedPnlAscending] = useState(false);
    const [detailSortBy, setDetailSortBy] = useState<DetailSortBy>("profit");
    const [detailSortAscending, setDetailSortAscending] = useState(false);
    const [summaryRows, setSummaryRows] = useState<RealizedReportRow[]>([]);
    const [detailRows, setDetailRows] = useState<RealizedReportDetailRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    function fetchReports() {
        setLoading(true);
        setError("");
        fetchReportData(from, to, clientId).then(([summary, detail]) => {
            setSummaryRows(summary);
            setDetailRows(detail);
        }).catch(err => {
            console.error(err);
            setSummaryRows([]);
            setDetailRows([]);
            setError("Failed to fetch reports");
        }).finally(() => setLoading(false));
    }

    useEffect(() => {
        setLoading(true);
        fetchReportData(DEFAULT_REPORT_FROM, DEFAULT_REPORT_TO, DEFAULT_REPORT_CLIENT_ID).then(([summary, detail]) => {
            setSummaryRows(summary);
            setDetailRows(detail);
        }).catch(err => {
            console.error(err);
            setSummaryRows([]);
            setDetailRows([]);
            setError("Failed to fetch reports");
        }).finally(() => setLoading(false));
    }, []);

    const normalizedSymbolFilter = symbolFilter.trim().toLowerCase();
    const filteredSummaryRows = summaryRows
        .filter(row => normalizedSymbolFilter === "" || row.symbol.toLowerCase().includes(normalizedSymbolFilter))
        .sort((left, right) => realizedPnlAscending ? left.realizedPnl - right.realizedPnl : right.realizedPnl - left.realizedPnl);
    const filteredDetailRows = detailRows
        .filter(row => normalizedSymbolFilter === "" || row.symbol.toLowerCase().includes(normalizedSymbolFilter))
        .filter(row => entryDateFilter === "" || row.entryDate.includes(entryDateFilter))
        .filter(row => exitDateFilter === "" || row.exitDate.includes(exitDateFilter))
        .sort((left, right) => {
            const leftValue = left[detailSortBy];
            const rightValue = right[detailSortBy];
            const comparison = typeof leftValue === "number"
                ? leftValue - (rightValue as number)
                : leftValue.localeCompare(rightValue as string);
            return detailSortAscending ? comparison : -comparison;
        });

    function sortDetail(sortBy: DetailSortBy) {
        if (detailSortBy === sortBy) {
            setDetailSortAscending(!detailSortAscending);
        } else {
            setDetailSortBy(sortBy);
            setDetailSortAscending(false);
        }
    }

    const summaryQuantity = filteredSummaryRows.reduce((sum, row) => sum + row.quantity, 0);
    const summaryBuyValue = filteredSummaryRows.reduce((sum, row) => sum + row.buyValue, 0);
    const summarySellValue = filteredSummaryRows.reduce((sum, row) => sum + row.sellValue, 0);
    const summaryRealizedPnl = filteredSummaryRows.reduce((sum, row) => sum + row.realizedPnl, 0);

    const detailQuantity = filteredDetailRows.reduce((sum, row) => sum + row.quantity, 0);
    const detailBuyValue = filteredDetailRows.reduce((sum, row) => sum + row.buyValue, 0);
    const detailSellValue = filteredDetailRows.reduce((sum, row) => sum + row.sellValue, 0);
    const detailProfit = filteredDetailRows.reduce((sum, row) => sum + row.profit, 0);

    return <>
        <label>
            client
            <input type="text" value={clientId} onChange={event => setClientId(event.target.value)}/>
        </label>
        &emsp;
        <label>
            from
            <input type="date" value={from} onChange={event => setFrom(event.target.value)}/>
        </label>
        &emsp;
        <label>
            to
            <input type="date" value={to} onChange={event => setTo(event.target.value)}/>
        </label>
        &emsp;
        <label>
            symbol
            <input type="text" value={symbolFilter} onChange={event => setSymbolFilter(event.target.value)}/>
        </label>
        &emsp;
        <label>
            entryDate
            <input type="text" value={entryDateFilter} onChange={event => setEntryDateFilter(event.target.value)}/>
        </label>
        &emsp;
        <label>
            exitDate
            <input type="text" value={exitDateFilter} onChange={event => setExitDateFilter(event.target.value)}/>
        </label>
        &emsp;
        <button onClick={fetchReports} disabled={loading}>{loading ? "loading" : "refresh"}</button>
        {error ? <span className="red">&emsp;{error}</span> : <></>}
        <h3>Realized Report</h3>
        <table className={"full"}>
            <thead>
            <tr>
                <th>symbol</th>
                <th>quantity</th>
                <th>buyValue</th>
                <th>sellValue</th>
                <th onClick={() => setRealizedPnlAscending(!realizedPnlAscending)}>
                    realizedPnl {realizedPnlAscending ? "asc" : "desc"}
                </th>
            </tr>
            </thead>
            <tbody>
            {filteredSummaryRows.map(row =>
                <tr key={row.symbol}>
                    <td>{row.symbol}</td>
                    <td>{row.quantity}</td>
                    <td>{formatMoney(row.buyValue)}</td>
                    <td>{formatMoney(row.sellValue)}</td>
                    <td className={row.realizedPnl >= 0 ? "green" : "red"}>{formatMoney(row.realizedPnl)}</td>
                </tr>
            )}
            <tr>
                <td>total</td>
                <td>{summaryQuantity}</td>
                <td>{formatMoney(summaryBuyValue)}</td>
                <td>{formatMoney(summarySellValue)}</td>
                <td className={summaryRealizedPnl >= 0 ? "green" : "red"}>{formatMoney(summaryRealizedPnl)}</td>
            </tr>
            </tbody>
        </table>

        <h3>Realized Report Detail</h3>
        <table className={"full"}>
            <thead>
            <tr>
                <th>symbol</th>
                <th>isin</th>
                <th onClick={() => sortDetail("entryDate")}>
                    entryDate {detailSortBy === "entryDate" ? (detailSortAscending ? "asc" : "desc") : ""}
                </th>
                <th onClick={() => sortDetail("exitDate")}>
                    exitDate {detailSortBy === "exitDate" ? (detailSortAscending ? "asc" : "desc") : ""}
                </th>
                <th>quantity</th>
                <th>buyValue</th>
                <th>sellValue</th>
                <th onClick={() => sortDetail("profit")}>
                    profit {detailSortBy === "profit" ? (detailSortAscending ? "asc" : "desc") : ""}
                </th>
                <th>periodOfHolding</th>
            </tr>
            </thead>
            <tbody>
            {filteredDetailRows.map((row, index) =>
                <tr key={`${row.symbol}-${row.isin}-${row.entryDate}-${row.exitDate}-${index}`}>
                    <td>{row.symbol}</td>
                    <td>{row.isin}</td>
                    <td>{row.entryDate}</td>
                    <td>{row.exitDate}</td>
                    <td>{row.quantity}</td>
                    <td>{formatMoney(row.buyValue)}</td>
                    <td>{formatMoney(row.sellValue)}</td>
                    <td className={row.profit >= 0 ? "green" : "red"}>{formatMoney(row.profit)}</td>
                    <td>{row.periodOfHolding}</td>
                </tr>
            )}
            <tr>
                <td>total</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>{detailQuantity}</td>
                <td>{formatMoney(detailBuyValue)}</td>
                <td>{formatMoney(detailSellValue)}</td>
                <td className={detailProfit >= 0 ? "green" : "red"}>{formatMoney(detailProfit)}</td>
                <td>-</td>
            </tr>
            </tbody>
        </table>
    </>
}
